import {
  formatCurrencyCompact,
  formatCurrencyExact as formatCurrencyExactShared,
  formatSignedPct,
  trimStr,
} from '../cfoFormat'
import type { CeoBasisKind, CeoPolarity } from './ceoCockpitTypes'

export { formatCurrencyCompact, formatCurrencyExactShared as formatCurrencyExact, formatSignedPct, trimStr }

export const BASIS_LABEL: Record<CeoBasisKind, string> = {
  plan: 'plan',
  forecast: 'forecast',
  prior_year: 'prior year',
  prior_period: 'prior period',
}

export const PERIOD_LABEL: Record<'mtd' | 'qtd' | 'ytd', string> = {
  mtd: 'Month to date',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
}

/** Polarity-aware delta grammar: "▲ $12,400 · +4.2% vs plan". */
export function formatDeltaGrammar(args: {
  deltaAbs?: number | null
  deltaPct?: number | null
  basis: CeoBasisKind
  polarity?: CeoPolarity
  compact?: boolean
}): string {
  const abs = args.deltaAbs
  const pct = args.deltaPct
  if (abs == null || !Number.isFinite(abs)) {
    if (pct == null || !Number.isFinite(pct)) return `Not prepared vs ${BASIS_LABEL[args.basis]}`
  }
  const signed = signedDelta(abs ?? 0, args.polarity)
  const arrow = signed > 0 ? '\u25B2' : signed < 0 ? '\u25BC' : '\u25C6'
  const magnitude = Math.abs(abs ?? 0)
  const money = args.compact ? formatCurrencyCompact(magnitude) : formatCurrencyExactShared(magnitude)
  const pctPart = pct != null && Number.isFinite(pct) ? ` \u00B7 ${formatSignedPct(pct)}` : ''
  return `${arrow} ${money}${pctPart} vs ${BASIS_LABEL[args.basis]}`
}

export function computeDeltaGrammar(args: {
  deltaAbs?: number | null
  deltaPct?: number | null
  polarity?: CeoPolarity
}): { signed: number; tone: 'pos' | 'neg' | 'flat'; arrow: string } {
  const signed = signedDelta(args.deltaAbs ?? 0, args.polarity)
  if (signed > 0) return { signed, tone: 'pos', arrow: '\u25B2' }
  if (signed < 0) return { signed, tone: 'neg', arrow: '\u25BC' }
  return { signed, tone: 'flat', arrow: '\u25C6' }
}

export function formatRankShift(deltaRank?: number | null): string {
  if (deltaRank == null || !Number.isFinite(deltaRank) || deltaRank === 0) return 'unchanged'
  const n = Math.abs(Math.round(deltaRank))
  if (deltaRank < 0) return `\u2191 ${n}`
  return `\u2193 ${n}`
}

export function formatSignedExactCurrency(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${formatCurrencyExactShared(Math.abs(value))}`
}

export function formatSignedCompactCurrency(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${formatCurrencyCompact(Math.abs(value))}`
}

export function formatPctPoints(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return 'Not prepared'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${(Math.abs(value) * 100).toFixed(digits)} pts`
}

export function formatShare(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return 'Not prepared'
  return `${(value * 100).toFixed(1)}%`
}

export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return 'Not prepared'
  return Math.round(value).toLocaleString('en-US')
}

export function formatMonths(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return 'Not prepared'
  const rounded = Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(1)
  return `${rounded} mo`
}

export function formatDays(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return 'Not prepared'
  return `${Math.round(value)} days`
}

function signedDelta(deltaAbs: number, polarity?: CeoPolarity): number {
  if (polarity === 'lower_is_better') return -deltaAbs
  return deltaAbs
}
